/* =========================================================================
   ENDEX GROUP — endex-fx.js | Rev 04 "Formation" motion engine
   GSAP + ScrollTrigger + Lenis. Everything degrades to static, fully visible
   content without JS or with prefers-reduced-motion.
   ========================================================================= */
(function(){
  'use strict';
  var html = document.documentElement;
  var REDUCE = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var CFG = window.EndexFXConfig || {};
  var SIG = CFG.sig || getComputedStyle(html).getPropertyValue('--sig').trim() || '#FF5A1F';
  var DPR = Math.min(window.devicePixelRatio || 1, 2);
  var $ = function(s, r){ return (r||document).querySelector(s); };
  var $$ = function(s, r){ return Array.prototype.slice.call((r||document).querySelectorAll(s)); };
  var clamp = function(v,a,b){ return v<a?a:v>b?b:v; };
  var lerp = function(a,b,t){ return a+(b-a)*t; };
  var seg = function(p,a,b){ return clamp((p-a)/(b-a),0,1); };
  var eio = function(t){ return t<.5?4*t*t*t:1-Math.pow(-2*t+2,3)/2; };
  var eo = function(t){ return 1-Math.pow(1-t,3); };
  function rgb(hex){ hex=hex.replace('#',''); if(hex.length===3) hex=hex.split('').map(function(c){return c+c;}).join(''); var n=parseInt(hex,16); return [n>>16&255,n>>8&255,n&255]; }
  var SIGRGB = rgb(SIG);
  function ready(fn){ document.readyState!=='loading' ? fn() : document.addEventListener('DOMContentLoaded', fn); }
  function mulberry(a){ return function(){ a|=0; a=a+0x6D2B79F5|0; var t=Math.imul(a^a>>>15,1|a); t=t+Math.imul(t^t>>>7,61|t)^t; return ((t^t>>>14)>>>0)/4294967296; }; }

  /* ---------------------------------------------------------------- utils */
  var GLYPHS = '▓▒░█#/\\<>_-=+*01';
  function scramble(el, text, dur){
    if(!el) return; text = text==null ? (el.dataset.scramble || el.textContent) : text;
    el.dataset.scramble = text;
    if(REDUCE || !window.gsap){ el.textContent = text; return; }
    var o={p:0}, len=text.length;
    if(el._scr) el._scr.kill();
    el._scr = gsap.to(o,{p:1,duration:dur||Math.min(1.1,.35+len*.02),ease:'none',onUpdate:function(){
      var out='', rev=Math.floor(o.p*len*1.15);
      for(var i=0;i<len;i++){ var ch=text[i]; if(ch===' '||i<rev) out+=ch; else out+= Math.random()<.5 ? GLYPHS[(Math.random()*GLYPHS.length)|0] : ch; }
      el.textContent=out;
    },onComplete:function(){ el.textContent=text; }});
  }
  /* wrap every word of an element in .fx-w spans, preserving inline markup */
  function splitWords(el){
    if(el._words) return el._words;
    var words=[];
    (function walk(node){
      $$(':scope > *', node); // noop to keep linter quiet
      var kids=Array.prototype.slice.call(node.childNodes);
      kids.forEach(function(k){
        if(k.nodeType===3){
          var parts=k.textContent.split(/(\s+)/), frag=document.createDocumentFragment();
          parts.forEach(function(pt){
            if(!pt) return;
            if(/^\s+$/.test(pt)){ frag.appendChild(document.createTextNode(' ')); return; }
            var s=document.createElement('span'); s.className='fx-w'; s.textContent=pt; frag.appendChild(s); words.push(s);
          });
          k.parentNode.replaceChild(frag,k);
        } else if(k.nodeType===1 && k.tagName!=='BR' && !k.classList.contains('fx-caret')) walk(k);
      });
    })(el);
    el._words=words; return words;
  }
  /* mask each word for a slide-up reveal */
  function maskWords(el){
    var w=splitWords(el);
    w.forEach(function(s){ if(s.parentNode.classList && s.parentNode.classList.contains('fx-wm')) return;
      var m=document.createElement('span'); m.className='fx-wm'; m.style.cssText='display:inline-block;overflow:hidden;vertical-align:top;padding-bottom:.06em;margin-bottom:-.06em';
      s.parentNode.insertBefore(m,s); m.appendChild(s); });
    return w;
  }
  function onVisible(el, cb, opts){
    if(!('IntersectionObserver' in window)){ cb(true); return; }
    var io=new IntersectionObserver(function(es){ es.forEach(function(e){ cb(e.isIntersecting); }); }, opts||{rootMargin:'100px'});
    io.observe(el); return io;
  }
  function fitCanvas(cv){
    var r=cv.getBoundingClientRect(), w=Math.max(1,Math.round(r.width*DPR)), h=Math.max(1,Math.round(r.height*DPR));
    if(cv.width!==w||cv.height!==h){ cv.width=w; cv.height=h; }
    return {w:w,h:h};
  }
  function loop(root, fn){
    var on=false, t0=performance.now();
    function tick(){ if(on) fn((performance.now()-t0)/1000); }
    onVisible(root, function(v){ if(v&&!on){ on=true; gsap.ticker.add(tick);} else if(!v&&on){ on=false; gsap.ticker.remove(tick);} });
    return function(){ fn((performance.now()-t0)/1000); };
  }

  /* ------------------------------------------------------------- world data */
  var landP=null;
  function getLand(){
    if(landP) return landP;
    landP = new Promise(function(res,rej){ if(window.EndexLand) return res(window.EndexLand); var sc=document.createElement('script'); sc.src=CFG.land; sc.async=true; sc.onload=function(){ res(window.EndexLand); }; sc.onerror=rej; document.head.appendChild(sc); }).then(function(topo){
      var f=topojson.feature(topo, topo.objects.land), rings=[];
      (f.features||[f]).forEach(function(ft){ var g=ft.geometry||ft;
        (g.type==='Polygon'?[g.coordinates]:g.coordinates).forEach(function(poly){ poly.forEach(function(r){ rings.push(r); }); }); });
      return rings;
    });
    return landP;
  }

  /* =================================================================== CORE */
  var lenis=null;
  function core(){
    gsap.registerPlugin(ScrollTrigger);
    if(!REDUCE && window.Lenis && CFG.smooth!==false){
      lenis = new Lenis({ lerp:.095, wheelMultiplier:1, anchors:{offset:0}, autoRaf:false });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function(t){ lenis.raf(t*1000); });
      gsap.ticker.lagSmoothing(0);
      window.EndexLenis = lenis;
    }
    // stop lenis in scrollable overlays
    $$('#cart-drawer [data-panel], .fxm, #mnav [data-panel]').forEach(function(p){ p.setAttribute('data-lenis-prevent',''); });
    new MutationObserver(function(){
      var open=$('#cart-drawer[open]')||$('#mnav[open]');
      if(lenis){ open?lenis.stop():(!html.classList.contains('fx-menu-open')&&!html.classList.contains('fx-load')&&lenis.start()); }
    }).observe(document.body,{subtree:true,attributes:true,attributeFilter:['open']});
  }

  /* ----------------------------------------------------------------- loader */
  function loader(){
    var el=$('.fxl'); if(!el) return Promise.resolve();
    if(!html.classList.contains('fx-load')){ el.remove(); return Promise.resolve(); }
    try{ sessionStorage.setItem('fxSeen','1'); }catch(e){}
    if(lenis) lenis.stop();
    var sq=$$('.fxl__sq',el), pct=$('.fxl__pct',el), t=$('.fxl__t',el);
    scramble(t, t.textContent, .8);
    return new Promise(function(res){
      var o={v:0};
      gsap.to(o,{v:100,duration:1.25,ease:'power2.inOut',onUpdate:function(){
        var n=Math.floor(o.v/20); sq.forEach(function(s,i){ s.classList.toggle('on', i<n || (i===n && Math.random()<.5)); });
        pct.textContent=String(Math.floor(o.v)).padStart(3,'0')+'%';
      },onComplete:function(){
        sq.forEach(function(s){ s.classList.add('on'); });
        var tl=gsap.timeline({onComplete:function(){ el.remove(); html.classList.remove('fx-load'); if(lenis) lenis.start(); }});
        tl.to(sq,{scaleY:12,duration:.45,ease:'expo.in',stagger:{each:.04,from:'center'}})
          .to([pct,t],{opacity:0,duration:.2},0)
          .to(el,{clipPath:'inset(0 0 100% 0)',duration:.85,ease:'expo.inOut'},'-=.05');
        setTimeout(res, 520);
      }});
    });
  }

  /* ----------------------------------------------------------------- header */
  function header(){
    var h=$('.fxh'); if(!h) return;
    var last=0;
    function upd(y){
      h.classList.toggle('is-scrolled', y>40);
      if(!html.classList.contains('fx-menu-open')) h.classList.toggle('is-hidden', y>last+2 && y>300);
      if(y<last-2) h.classList.remove('is-hidden');
      last=y;
    }
    if(lenis) lenis.on('scroll', function(e){ upd(e.scroll); }); else window.addEventListener('scroll', function(){ upd(window.pageYOffset); }, {passive:true});
    upd(window.pageYOffset);
    // menu
    var btn=$('[data-fx-menu]'), m=$('.fxm'); if(!btn||!m) return;
    var lab=$('.fxh__menu-l', btn);
    function setOpen(o){
      html.classList.toggle('fx-menu-open', o); m.classList.toggle('is-open', o); btn.setAttribute('aria-expanded', o);
      if(lab) scramble(lab, o?'CLOSE':'MENU', .4);
      if(lenis) o?lenis.stop():lenis.start(); else html.style.overflow=o?'hidden':'';
      if(o){ var f=$('a',m); f&&setTimeout(function(){ f.focus({preventScroll:true}); },400); }
    }
    btn.addEventListener('click', function(){ setOpen(!m.classList.contains('is-open')); });
    document.addEventListener('keydown', function(e){ if(e.key==='Escape' && m.classList.contains('is-open')) setOpen(false); });
    $$('a',m).forEach(function(a){ a.addEventListener('click', function(){ setOpen(false); }); });
  }

  /* ---------------------------------------------------- global auto effects */
  function globals(){
    // scramble labels on view
    $$('[data-fx="scramble"], .eyebrow, .fx-tag, .shead__t').forEach(function(el){
      if(el.closest('.fxl')) return;
      var target = el.matches('.eyebrow') ? (el.lastChild && el.lastChild.nodeType===3 ? el : null) : el;
      if(!target) return;
      var txtNode = null;
      if(el.matches('.eyebrow')){ txtNode=document.createElement('span'); txtNode.textContent=el.lastChild.textContent; el.replaceChild(txtNode, el.lastChild); target=txtNode; }
      var text=target.textContent;
      ScrollTrigger.create({trigger:el,start:'top 92%',once:true,onEnter:function(){ scramble(target,text); }});
    });
    // headline word slide-up (legacy + fx sections that opt in)
    $$('main .shopify-section:not(.fx-own) h1, main .shopify-section:not(.fx-own) h2, main .shopify-section:not(.fx-own) .display, [data-fx="rise"]').forEach(function(el){
      if(el.closest('[data-fx-skip]')||el.closest('.fx-own')&&!el.matches('[data-fx="rise"]')) return;
      var w=maskWords(el);
      gsap.set(w,{yPercent:110});
      ScrollTrigger.create({trigger:el,start:'top 88%',once:true,onEnter:function(){
        gsap.to(w,{yPercent:0,duration:1.05,ease:'expo.out',stagger:.045});
      }});
    });
    // chromatic scrub word reveal
    $$('[data-fx="words"]').forEach(function(el){ if(el.closest('[data-fxs]')&&el.hasAttribute('data-fx-manual')) return; wordScrub(el); });
    // parallax media
    $$('[data-fx="parallax"], main .shopify-section:not(.fx-own) .hero__media img, main .shopify-section:not(.fx-own) [class*="banner"] img, main .shopify-section:not(.fx-own) .imgband img').forEach(function(img){
      var sp=parseFloat(img.getAttribute('data-speed')||'.14');
      gsap.fromTo(img,{yPercent:-sp*50,scale:1+sp},{yPercent:sp*50,ease:'none',scrollTrigger:{trigger:img.parentNode,start:'top bottom',end:'bottom top',scrub:true}});
    });
    // clip reveals
    $$('[data-fx="clip"]').forEach(function(el){
      gsap.fromTo(el,{clipPath:'inset(18% 12% 18% 12%)'},{clipPath:'inset(0% 0% 0% 0%)',ease:'none',scrollTrigger:{trigger:el,start:'top 95%',end:'top 35%',scrub:true}});
    });
    // staggered cards (legacy grids)
    var cardSel='main .card, main .collcard, main .det, main .readout, main [class*="__tile"], main .citem, main .fx-stag';
    var cards=$$(cardSel).filter(function(c){ return !c.closest('.fx-own'); });
    if(cards.length){
      gsap.set(cards,{y:50,opacity:0});
      ScrollTrigger.batch(cards,{start:'top 92%',once:true,onEnter:function(b){ gsap.to(b,{y:0,opacity:1,duration:1,ease:'expo.out',stagger:.07,overwrite:true}); }});
    }
    // rules draw
    $$('.fx-rule, main hr').forEach(function(r){ gsap.fromTo(r,{scaleX:0},{scaleX:1,duration:1.4,ease:'expo.out',transformOrigin:'left',scrollTrigger:{trigger:r,start:'top 92%',once:true}}); });
    // counters
    $$('[data-fx="count"]').forEach(counter);
    // svg draw
    $$('[data-fx="draw"]').forEach(draw);
    // button label scramble on hover
    $$('.fx-btn, .btn').forEach(function(b){
      var span=$('[data-lbl]',b); if(!span) return; var t=span.textContent;
      b.addEventListener('mouseenter',function(){ scramble(span,t,.45); });
    });
  }
  function wordScrub(el, opt){
    opt=opt||{};
    var w=splitWords(el), caret=document.createElement('span'); caret.className='fx-caret';
    var hot=[], lastN=-1;
    function set(p){
      var n=Math.round(clamp(p,0,1)*w.length);
      if(n===lastN) return; lastN=n;
      for(var i=0;i<w.length;i++) w[i].classList.toggle('on', i<n);
      hot.forEach(function(h){ h.classList.remove('hot'); }); hot=[];
      if(n>0 && n<w.length){ for(var j=Math.max(0,n-3);j<n;j++){ w[j].classList.add('hot'); hot.push(w[j]); } w[n-1].after(caret); }
      else if(n>=w.length){ w[w.length-1].after(caret); }
      else caret.remove();
    }
    el._setWords=set; set(0);
    if(!opt.manual) ScrollTrigger.create({trigger:el,start:opt.start||'top 85%',end:opt.end||'bottom 45%',scrub:true,onUpdate:function(s){ set(s.progress); }});
    return set;
  }
  function counter(el){
    var raw=el.getAttribute('data-to')||el.textContent, m=raw.match(/^([^0-9]*)([0-9.,]+)(.*)$/);
    if(!m){ return; }
    var pre=m[1], num=parseFloat(m[2].replace(/,/g,'')), suf=m[3], dec=(m[2].split('.')[1]||'').length, o={v:0};
    el.textContent=pre+'0'+suf;
    ScrollTrigger.create({trigger:el,start:'top 88%',once:true,onEnter:function(){
      gsap.to(o,{v:num,duration:2.1,ease:'expo.out',onUpdate:function(){
        var s=o.v.toFixed(dec); if(o.v<num*.97) s=s.replace(/\d(?=\d{0,1}$)/,function(){ return (Math.random()*10)|0; });
        el.textContent=pre+Number(s).toLocaleString('en-US',{minimumFractionDigits:dec,maximumFractionDigits:dec})+suf;
      },onComplete:function(){ el.textContent=pre+num.toLocaleString('en-US',{minimumFractionDigits:dec})+suf; }});
    }});
  }
  function draw(svg){
    var all=$$('path,circle,line,ellipse,polyline,rect',svg), dotted=all.filter(function(p){ return p.hasAttribute('stroke-dasharray'); }), parts=all.filter(function(p){ return !p.hasAttribute('stroke-dasharray'); });
    if(dotted.length){ gsap.set(dotted,{opacity:0,scale:.6,transformOrigin:'50% 50%'}); ScrollTrigger.create({trigger:svg,start:'top 85%',once:true,onEnter:function(){ gsap.to(dotted,{opacity:1,scale:1,duration:1.4,ease:'expo.out',stagger:.08}); }}); }
    parts.forEach(function(p){ var L=p.getTotalLength?p.getTotalLength():300; p.style.strokeDasharray=L; p.style.strokeDashoffset=L; });
    ScrollTrigger.create({trigger:svg,start:'top 85%',once:true,onEnter:function(){
      gsap.to(parts,{strokeDashoffset:0,duration:1.8,ease:'power2.inOut',stagger:{each:.012,from:'random'}});
    }});
  }

  /* ================================================================ SECTIONS */
  var S={};

  /* ---- HERO: camo-blob erase → eye → black ------------------------------ */
  S.hero=function(root){
    var pin=$('.fx-pin',root), cv=$('.fxhero__cv',root), ctx=cv.getContext('2d'), ui=$('.fxhero__ui',root), media=$('.fxhero__media',root), tick=$('.fxhero__tick',root), rows=$$('div',tick);
    var rnd=mulberry(7), blobs=[], P=0;
    for(var i=0;i<30;i++){
      var x=rnd()*1.3-.15, y=rnd()*1.3-.15, dc=Math.hypot(x-.5,(y-.5)*.8), subs=[];
      var ns=2+((rnd()*3)|0); for(var j=0;j<ns;j++) subs.push({dx:(rnd()-.5)*.9,dy:(rnd()-.5)*.6,s:.45+rnd()*.55,ph:rnd()*6.28,k:2+((rnd()*2)|0)});
      blobs.push({x:x,y:y,r:.06+rnd()*.1,s:clamp(.6-dc*1.1+rnd()*.25,0,.75),lim:clamp((dc-.08)/.3,.18,1),subs:subs});
    }
    var CHEV=[[-.5375,-.7925],[-.375,-.955],[0,-.58],[.375,-.955],[.5375,-.7925],[0,-.255]];
    var spk=[]; for(var z=0;z<16;z++) spk.push({x:rnd()*2-1,y:rnd()*2-1,r:.02+rnd()*.05,a:rnd()*3});
    var base=rows.map(function(r){ return r.textContent; });
    function glitch(){
      rows.forEach(function(r,i){ var s=base[i].split(''); for(var k=0;k<s.length;k++){ if(s[k]!==' '&&Math.random()<.035) s[k]='█▓▒░'[(Math.random()*4)|0]; } r.textContent=s.join(''); });
    }
    function blob(b,W,H,g,t){
      var M=Math.max(W,H), R0=b.r*M*(.2+g*2.4*b.lim);
      b.subs.forEach(function(q){
        var cx=(b.x*W)+q.dx*R0, cy=(b.y*H)+q.dy*R0, R=R0*q.s, n=18, pts=[];
        for(var j=0;j<n;j++){ var a=j/n*6.2832, w=1+.16*Math.sin(a*q.k+q.ph+t*.45)+.07*Math.sin(a*5-t*.8+q.ph);
          pts.push([cx+Math.cos(a)*R*w*1.15, cy+Math.sin(a)*R*w*.8]); }
        ctx.beginPath(); var m0=[(pts[n-1][0]+pts[0][0])/2,(pts[n-1][1]+pts[0][1])/2]; ctx.moveTo(m0[0],m0[1]);
        for(var k=0;k<n;k++){ var c=pts[k], nx=pts[(k+1)%n]; ctx.quadraticCurveTo(c[0],c[1],(c[0]+nx[0])/2,(c[1]+nx[1])/2); }
        ctx.closePath(); ctx.fill();
      });
    }
    var lastG=0;
    var render=loop(root,function(t){
      var d=fitCanvas(cv), W=d.w, H=d.h, p=P;
      ctx.clearRect(0,0,W,H);
      if(p<=0.001) return;
      ctx.fillStyle='#000';
      var bp=seg(p,0,.5);
      blobs.forEach(function(b){ var g=eio(seg(bp,b.s*.8,1)); if(g>0) blob(b,W,H,g,t); });
      // ENDEX mark: four chevrons + core square open as windows onto the image, then fall in to the centre
      var e=eo(seg(p,.3,.52)), c=eio(seg(p,.62,.8)), c2=eio(seg(p,.76,.9));
      if(e>0){
        var cx=W/2, cy=H/2, S=Math.min(W*.3,H*.46)*(.85+.15*e)*(1-.9*c2), rot=0;
        function P2(x,y,k,sh){ // unit coords → canvas; k = quarter turns, sh = inward shift
          y+=sh; var a=k*Math.PI/2+rot, ca=Math.cos(a), sa=Math.sin(a); return [cx+(x*ca-y*sa)*S, cy+(x*sa+y*ca)*S]; }
        function markPath(){
          ctx.beginPath();
          for(var k=0;k<4;k++){ CHEV.forEach(function(q,j){ var r=P2(q[0],q[1],k,c*.065); j?ctx.lineTo(r[0],r[1]):ctx.moveTo(r[0],r[1]); }); ctx.closePath(); }
          var s=.1875; [[-s,-s],[s,-s],[s,s],[-s,s]].forEach(function(q,j){ var r=P2(q[0],q[1],0,0); j?ctx.lineTo(r[0],r[1]):ctx.moveTo(r[0],r[1]); }); ctx.closePath();
        }
        ctx.save(); ctx.globalAlpha=eo(seg(p,.34,.56)); ctx.fillRect(0,0,W,H); ctx.restore();
        ctx.save(); markPath(); ctx.globalCompositeOperation='destination-out'; ctx.globalAlpha=.94*e*(1-c2); ctx.fill('nonzero');
        ctx.globalCompositeOperation='source-over'; ctx.globalAlpha=1; ctx.clip('nonzero');
        ctx.fillStyle='#000'; spk.forEach(function(q){ var R=q.r*S*1.6*(.4+.6*e); ctx.beginPath(); ctx.ellipse(cx+q.x*S*1.1,cy+q.y*S*1.1,R*1.3,R*.8,q.a+t*.1,0,6.2832); ctx.fill(); });
        ctx.restore();
        // outline draws on, core fills white like a pupil
        ctx.save(); ctx.strokeStyle='rgba(244,241,234,'+(.95*e*(1-c2))+')'; ctx.lineWidth=1.5*DPR; ctx.lineJoin='miter';
        var per=S*3.4; ctx.setLineDash([per*e, per]); markPath(); ctx.stroke(); ctx.setLineDash([]);
        var ps=.1875*.55*S*(1+c*.8); ctx.fillStyle='rgba(255,255,255,'+(e*(1-c2))+')';
        ctx.translate(cx,cy); ctx.rotate(rot); ctx.fillRect(-ps,-ps,ps*2,ps*2);
        // HUD ring + ticks
        ctx.rotate(-rot+t*.15); ctx.strokeStyle='rgba(244,241,234,'+(.28*e*(1-c))+')'; ctx.lineWidth=1*DPR; ctx.setLineDash([2*DPR,9*DPR]);
        ctx.beginPath(); ctx.arc(0,0,S*1.32,0,6.2832); ctx.stroke(); ctx.setLineDash([]);
        for(var q=0;q<4;q++){ ctx.rotate(Math.PI/2); ctx.beginPath(); ctx.moveTo(S*1.24,0); ctx.lineTo(S*1.42,0); ctx.stroke(); }
        ctx.restore();
      }
      if(p>.86){ ctx.fillStyle='rgba(0,0,0,'+eo(seg(p,.86,.95))+')'; ctx.fillRect(0,0,W,H); }
      // ticker
      var to=seg(p,.12,.22)*(1-seg(p,.72,.8));
      tick.style.opacity=to;
      if(to>0){ rows.forEach(function(r,i){ var dir=i%2?1:-1; r.style.transform='translate3d('+(dir*(p*1400+t*38)%1200-(i%2?1200:0))+'px,0,0)'; });
        if(t-lastG>.07){ lastG=t; glitch(); } }
    });
    gsap.timeline({scrollTrigger:{trigger:pin,start:'top top',end:'+=240%',pin:true,scrub:true,onUpdate:function(s){ P=s.progress; render(); }}})
      .to(ui,{yPercent:-12,opacity:0,ease:'power1.in',duration:.2},0)
      .to(media,{scale:1.18,ease:'none',duration:.8},0)
      .to({}, {duration:.2});
    // intro
    var h=$('.fxhero__h',root);
    if(h){ var w=maskWords(h); gsap.from(w,{yPercent:110,duration:1.3,ease:'expo.out',stagger:.06,delay:html.classList.contains('fx-load')?1.7:.2}); }
    gsap.from(media,{scale:1.25,duration:2.4,ease:'expo.out',delay:html.classList.contains('fx-load')?1.5:0});
    $$('.fxhero__meta > div, .fxhero__sub, .fxhero__cta',root).forEach(function(el,i){ gsap.from(el,{y:24,opacity:0,duration:1,ease:'expo.out',delay:(html.classList.contains('fx-load')?2:.5)+i*.08}); });
    $$('.fxhero__meta span',root).forEach(function(s,i){ setTimeout(function(){ scramble(s); }, (html.classList.contains('fx-load')?2000:500)+i*90); });
  };

  /* ---- GLOBE renderer --------------------------------------------------- */
  function Globe(cv){
    var ctx=cv.getContext('2d'), rings=null, self=this;
    getLand().then(function(r){ rings=r; }).catch(function(){});
    this.draw=function(rot,tilt,alpha){
      var d=fitCanvas(cv), W=d.w, H=d.h, R=Math.min(W,H)*.46, cx=W/2, cy=H/2;
      ctx.clearRect(0,0,W,H); if(alpha<=0) return;
      var l0=rot*Math.PI/180, p0=tilt*Math.PI/180, cp=Math.cos(p0), sp=Math.sin(p0);
      function pr(lon,lat){ var L=lon*Math.PI/180-l0, F=lat*Math.PI/180, cf=Math.cos(F);
        return [cx+R*cf*Math.sin(L), cy-R*(cp*Math.sin(F)-sp*cf*Math.cos(L)), sp*Math.sin(F)+cp*cf*Math.cos(L)]; }
      function line(pts){ var down=false; ctx.beginPath(); for(var i=0;i<pts.length;i++){ var q=pr(pts[i][0],pts[i][1]); if(q[2]>0){ down?ctx.lineTo(q[0],q[1]):ctx.moveTo(q[0],q[1]); down=true; } else down=false; } ctx.stroke(); }
      ctx.globalAlpha=alpha; ctx.lineJoin='round';
      // glow disc
      var g=ctx.createRadialGradient(cx,cy,R*.2,cx,cy,R*1.25); g.addColorStop(0,'rgba('+SIGRGB+',.10)'); g.addColorStop(.8,'rgba('+SIGRGB+',.04)'); g.addColorStop(1,'rgba('+SIGRGB+',0)');
      ctx.fillStyle=g; ctx.beginPath(); ctx.arc(cx,cy,R*1.25,0,6.2832); ctx.fill();
      ctx.strokeStyle='rgba('+SIGRGB+',.22)'; ctx.lineWidth=.8*DPR;
      for(var lon=-180;lon<180;lon+=10){ var a=[]; for(var la=-90;la<=90;la+=4) a.push([lon,la]); line(a); }
      for(var lat=-80;lat<=80;lat+=10){ var b=[]; for(var lo=-180;lo<=180;lo+=4) b.push([lo,lat]); line(b); }
      ctx.strokeStyle='rgba('+SIGRGB+',.55)'; ctx.lineWidth=1*DPR; ctx.beginPath(); ctx.arc(cx,cy,R,0,6.2832); ctx.stroke();
      if(rings){
        ctx.strokeStyle='rgba('+SIGRGB+',.35)'; ctx.lineWidth=3*DPR; rings.forEach(line);
        ctx.strokeStyle='rgba(255,'+Math.min(255,SIGRGB[1]+90)+','+Math.min(255,SIGRGB[2]+60)+',.95)'; ctx.lineWidth=1.1*DPR; rings.forEach(line);
      }
      ctx.globalAlpha=1;
    };
  }

  /* ---- MANIFESTO: globe + chromatic word typing ------------------------- */
  S.manifesto=function(root){
    var pin=$('.fx-pin',root), gw=$('.fxman__globe',root), globe=new Globe($('canvas',gw)), txt=$('[data-fx="words"]',root);
    var set=wordScrub(txt,{manual:true}), P=0;
    var render=loop(root,function(t){ globe.draw(t*9+P*120, 18-P*10, 1); });
    gsap.set(gw,{scale:.32,yPercent:-50,xPercent:-50,left:'50%',top:'50%'});
    gsap.timeline({scrollTrigger:{trigger:pin,start:'top top',end:'+=170%',pin:true,scrub:true,onUpdate:function(s){ P=s.progress; set(seg(P,.08,.82)); render(); }}})
      .to(gw,{scale:1.25,ease:'power1.inOut',duration:1},0)
      .to(gw,{opacity:.55,duration:.3},.7);
  };

  /* ---- STATEMENT -------------------------------------------------------- */
  S.statement=function(root){
    var img=$('.fxst__media',root), sh=$('.fxst__shade',root);
    gsap.fromTo(img,{clipPath:'inset(100% 0% 0% 0%)'},{clipPath:'inset(0% 0% 0% 0%)',ease:'none',scrollTrigger:{trigger:root,start:'top bottom',end:'top 15%',scrub:true}});
    gsap.fromTo($('img',img),{yPercent:-8,scale:1.2},{yPercent:12,scale:1.05,ease:'none',scrollTrigger:{trigger:root,start:'top bottom',end:'bottom top',scrub:true}});
    gsap.fromTo(sh,{opacity:.6},{opacity:1,ease:'none',scrollTrigger:{trigger:root,start:'top top',end:'center top',scrub:true}});
    $$('.fx-line > span',root).forEach(function(l,i){ gsap.fromTo(l,{yPercent:105},{yPercent:0,duration:1.2,ease:'expo.out',delay:i*.08,scrollTrigger:{trigger:root,start:'top 60%',once:true}}); });
  };

  /* ---- SHOWCASE: pinned 01|05 stack ------------------------------------- */
  var clothT=null, clothD=null;
  function clothDefs(){
    if($('#fx-cloth')) return;
    var d=document.createElement('div'); d.style.cssText='position:absolute;width:0;height:0;overflow:hidden';
    d.innerHTML='<svg width="0" height="0" aria-hidden="true"><filter id="fx-cloth" x="-10%" y="-10%" width="120%" height="120%"><feTurbulence type="fractalNoise" baseFrequency="0.004 0.009" numOctaves="2" seed="4" result="n"/><feDisplacementMap in="SourceGraphic" in2="n" scale="22" xChannelSelector="R" yChannelSelector="G"/></filter></svg>';
    document.body.appendChild(d); clothT=$('#fx-cloth feTurbulence'); clothD=$('#fx-cloth feDisplacementMap');
  }
  S.showcase=function(root){
    clothDefs();
    var pin=$('.fx-pin',root), cards=$$('.fxsc__card',root), bgs=$$('.fxsc__bg',root), items=$$('.fxsc__item',root), idx=$$('.fxsc__idx a',root),
        cnt=$('.fxsc__count',root), sqs=$$('.fxsc__sq',root), frame=$('.fxsc__frame',root), n=cards.length, I0=.14, cur=-1, P=0, vel=0, st;
    if(!n) return;
    function layout(){
      var vh=pin.clientHeight, q=seg(P,I0,1)*(n-1), intro=seg(P,0,I0);
      cards.forEach(function(c,i){
        var d=i-q, ad=Math.abs(d), inn=$('.fxsc__card-in',c);
        var mob=window.innerWidth<900, y=d*vh*(mob?1:.72), rx=clamp(-d*28,-45,45), sc=1-Math.min(ad,1.4)*.16;
        var show = i===0 ? eo(seg(intro,.55,1)) : 1;
        c.style.transform='translate3d(0,'+y+'px,0) scale('+(sc*(i===0?lerp(.18,1,show):1))+')';
        c.style.opacity= (i===0? show:1) * (mob ? 1-clamp(ad*1.4,0,1) : 1-clamp(ad-1.35,0,1));
        c.style.zIndex=100-Math.round(ad*10);
        inn.style.transform='rotateX('+rx+'deg)';
        var img=$('img',c); if(img) img.classList.toggle('fxsc__cloth', ad<.6 && !REDUCE);
      });
      bgs.forEach(function(b,i){ b.style.opacity=(1-clamp(Math.abs(i-q),0,1))*(i===0?Math.max(.0,eo(seg(intro,.4,1))):1); });
      // intro squares: row → column → collapse
      var a=eio(seg(intro,0,.45)), b=eio(seg(intro,.45,.8));
      sqs.forEach(function(s,i){ var o=i-(sqs.length-1)/2; s.style.transform='translate('+(o*34*(1-a))+'px,'+(o*34*a*1.6*(1-b))+'px) scale('+(1+b*3)+')'; s.style.opacity=1-seg(intro,.72,.95); });
      frame.style.opacity=.35+.65*eo(seg(intro,0,.3));
      var k=clamp(Math.round(q),0,n-1);
      if(k!==cur){ cur=k; items.forEach(function(it,i){ it.classList.toggle('on',i===k); }); idx.forEach(function(a,i){ a.classList.toggle('on',i===k); });
        var it=items[k]; if(it){ scramble($('h3 [data-t]',it)); scramble($('.fx-btn [data-lbl]',it),null,.5); gsap.fromTo($$('p,.fx-btn',it),{y:18,opacity:0},{y:0,opacity:1,duration:.8,ease:'expo.out',stagger:.06,overwrite:true}); }
        if(cnt) scramble(cnt,String(k+1).padStart(2,'0')+'  |  '+String(n).padStart(2,'0'),.4);
      }
    }
    var render=loop(root,function(t){
      if(clothT){ var a=.004+Math.sin(t*.7)*.0012, b=.009+Math.cos(t*.5)*.002; clothT.setAttribute('baseFrequency',a.toFixed(5)+' '+b.toFixed(5)); clothD.setAttribute('scale',(18+Math.min(40,Math.abs(vel)*.01)).toFixed(1)); }
      cards.forEach(function(c){ var inn=$('.fxsc__card-in',c); if(inn.style.transform.indexOf('rotateY')<0) inn.style.transform+=' rotateY(0deg)'; inn.style.transform=inn.style.transform.replace(/rotateY\([^)]*\)/,'rotateY('+(Math.sin(t*.8)*5).toFixed(2)+'deg)'); });
    });
    st=ScrollTrigger.create({trigger:pin,start:'top top',end:'+='+(n*85+60)+'%',pin:true,scrub:true,onUpdate:function(s){ P=s.progress; vel=s.getVelocity(); layout(); render(); },onRefresh:function(s){ P=s.progress; layout(); }});
    idx.forEach(function(a,i){ a.addEventListener('click',function(e){ e.preventDefault(); var y=st.start+(I0+(i/(Math.max(1,n-1)))*(1-I0))*(st.end-st.start)+2; lenis?lenis.scrollTo(y,{duration:1.4}):window.scrollTo({top:y,behavior:'smooth'}); }); });
    layout();
  };

  /* ---- CONCEPT: particles → glowing distribution curves ----------------- */
  S.concept=function(root){
    var pin=$('.fx-pin',root), cv=$('.fxcn__cv',root), ctx=cv.getContext('2d'), h=$('.fxcn__h',root), panel=$('.fxcn__panel',root), P=0;
    var cfg=JSON.parse($('script[type="application/json"]',root).textContent);
    var curves=cfg.curves, xl=cfg.x, yl=cfg.y, rnd=mulberry(11), parts=[];
    function gauss(c,x){ return c.h*Math.exp(-Math.pow(x-c.c,2)/(2*c.w*c.w)); }
    curves.forEach(function(c,k){ c.rgb=rgb(c.col); for(var i=0;i<380;i++){
      var u=clamp(c.c+(rnd()+rnd()+rnd()-1.5)*c.w*2.1,-1,1), onLine=rnd()<.35, ty=onLine?gauss(c,u):gauss(c,u)*Math.pow(rnd(),.7);
      parts.push({k:k,sx:rnd()*2-1,sy:rnd()*1.05,sz:rnd()-.5,tx:u,ty:ty,tz:c.z+(rnd()-.5)*.05,d:rnd()*.25}); } });
    var yaw=-.62, pitch=.3;
    function proj(x,y,z,W,H,off){ var cy=Math.cos(yaw), sy=Math.sin(yaw), X=x*cy-z*sy, Z=x*sy+z*cy, cp=Math.cos(pitch), sp=Math.sin(pitch), Y=y-.45, Y2=Y*cp-Z*sp, Z2=Y*sp+Z*cp, dd=3.4, s=Math.min(W*.42,H*.8)*dd/(Z2+dd);
      return [W*.52+off+X*s*1.05, H*.6-Y2*s]; }
    var render=loop(root,function(t){
      var d=fitCanvas(cv), W=d.w, H=d.h, p=P; ctx.clearRect(0,0,W,H);
      var off=-seg(p,.7,.9)*W*.2*(W>900*DPR?1:0), ym=W<900*DPR?-H*.12*seg(p,.7,.9):0;
      var P2=function(x,y,z){ var q=proj(x,y,z,W,H,off); q[1]+=ym; return q; };
      var boxA=seg(p,.04,.2);
      // box + floor grid
      ctx.lineWidth=1*DPR; ctx.strokeStyle='rgba(244,241,234,'+(.28*boxA)+')';
      var V=[[-1,0,-.5],[1,0,-.5],[1,0,.5],[-1,0,.5],[-1,1.05,-.5],[1,1.05,-.5],[1,1.05,.5],[-1,1.05,.5]], E=[[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7]];
      E.forEach(function(e){ var a=P2.apply(null,V[e[0]]), b=P2.apply(null,V[e[1]]); ctx.beginPath(); ctx.moveTo(a[0],a[1]); ctx.lineTo(b[0],b[1]); ctx.stroke(); });
      ctx.strokeStyle='rgba(244,241,234,'+(.08*boxA)+')';
      for(var gx=-1;gx<=1.001;gx+=.25){ var a=P2(gx,0,-.5), b=P2(gx,0,.5); ctx.beginPath(); ctx.moveTo(a[0],a[1]); ctx.lineTo(b[0],b[1]); ctx.stroke(); }
      [.35,.7].forEach(function(yy,i){ var a=P2(-1,yy,.5), b=P2(1,yy,.5), c2=P2(1,yy,-.5); ctx.beginPath(); ctx.moveTo(a[0],a[1]); ctx.lineTo(b[0],b[1]); ctx.lineTo(c2[0],c2[1]); ctx.stroke();
        ctx.fillStyle='rgba(244,241,234,'+(.6*boxA)+')'; ctx.font=(9*DPR)+'px JetBrains Mono,monospace'; ctx.textAlign='right'; ctx.fillText(yl[i]||'',a[0]-8*DPR,a[1]+3*DPR); });
      ctx.textAlign='center'; ctx.fillStyle='rgba(244,241,234,'+(.7*boxA)+')';
      xl.forEach(function(lb,i){ var x=-1+i*(2/(xl.length-1)), q=P2(x,0,.5); ctx.fillText(lb,q[0],q[1]+22*DPR); });
      // fills
      var fA=seg(p,.44,.66);
      if(fA>0){ ctx.globalCompositeOperation='lighter';
        curves.slice().sort(function(a,b){ return a.z-b.z; }).forEach(function(c){
          var top=[], i, x; for(i=0;i<=80;i++){ x=-1+i/40; top.push(P2(x,gauss(c,x)*eo(fA),c.z)); }
          var b0=P2(-1,0,c.z), b1=P2(1,0,c.z), pk=P2(c.c,c.h,c.z);
          var gr=ctx.createLinearGradient(0,pk[1],0,b0[1]); gr.addColorStop(0,'rgba('+c.rgb+','+(.92*fA)+')'); gr.addColorStop(.55,'rgba('+c.rgb+','+(.45*fA)+')'); gr.addColorStop(1,'rgba('+c.rgb+','+(.05*fA)+')');
          ctx.fillStyle=gr; ctx.beginPath(); ctx.moveTo(b0[0],b0[1]); top.forEach(function(q){ ctx.lineTo(q[0],q[1]); }); ctx.lineTo(b1[0],b1[1]); ctx.closePath(); ctx.fill();
          ctx.strokeStyle='rgba('+c.rgb+','+fA+')'; ctx.lineWidth=1.5*DPR; ctx.beginPath(); top.forEach(function(q,j){ j?ctx.lineTo(q[0],q[1]):ctx.moveTo(q[0],q[1]); }); ctx.stroke();
          // peak label
          var la=seg(p,.58,.7); if(la>0){ ctx.globalCompositeOperation='source-over'; ctx.strokeStyle='rgba('+c.rgb+','+la+')'; ctx.lineWidth=1*DPR; ctx.beginPath(); ctx.moveTo(pk[0],pk[1]); ctx.lineTo(pk[0],pk[1]-26*DPR); ctx.stroke();
            ctx.fillStyle='rgba('+c.rgb+','+la+')'; ctx.fillRect(pk[0]-2*DPR,pk[1]-2*DPR,4*DPR,4*DPR); ctx.font='600 '+(9.5*DPR)+'px JetBrains Mono,monospace'; ctx.fillText(c.label,pk[0],pk[1]-32*DPR); ctx.globalCompositeOperation='lighter'; }
        }); ctx.globalCompositeOperation='source-over'; }
      // particles
      var pa=seg(p,.08,.2)*(1-seg(p,.6,.75)), mv=seg(p,.2,.55);
      if(pa>0){ parts.forEach(function(q){ var c=curves[q.k], m=eio(clamp((mv-q.d)/(1-q.d),0,1)), jx=Math.sin(t*1.3+q.d*40)*.01*(1-m);
        var s=P2(lerp(q.sx,q.tx,m)+jx,lerp(q.sy,q.ty,m),lerp(q.sz,q.tz,m)); ctx.fillStyle='rgba('+c.rgb+','+(pa*(.5+.5*m))+')'; ctx.fillRect(s[0],s[1],1.6*DPR,1.6*DPR); }); }
    });
    ScrollTrigger.create({trigger:pin,start:'top top',end:'+=230%',pin:true,scrub:true,onUpdate:function(s){ P=s.progress; h.classList.toggle('on',P>.03); render(); }});
    gsap.timeline({scrollTrigger:{trigger:pin,start:'top top',end:'+=230%',scrub:true}})
      .fromTo(panel,{xPercent:101},{xPercent:0,ease:'power3.inOut',duration:.2},.7)
      .fromTo($$('.fxcn__chips span, .fxcn__panel .fx-tag, .fxcn__panel .fx-lead, .fxcn__panel .fx-body',root),{y:40,opacity:0},{y:0,opacity:1,stagger:.02,duration:.1},.8)
      .to({}, {duration:.1},.9);
    var hw=maskWords(h); gsap.from(hw,{yPercent:110,duration:1.1,ease:'expo.out',stagger:.08,scrollTrigger:{trigger:root,start:'top 70%',once:true}});
  };

  /* ---- VENN: pixel dissolve → dot map → formation rings ----------------- */
  S.venn=function(root){
    var pin=$('.fx-pin',root), mapC=$('.fxvn__map',root), pxC=$('.fxvn__px',root), rgC=$('.fxvn__rings',root), mx=mapC.getContext('2d'), px=pxC.getContext('2d'), rg=rgC.getContext('2d'),
        head=$('.fxvn__h',root), end=$('.fxvn__end',root), lbls=$$('.fxvn__lbl',root), P=0, rnd=mulberry(3), mapReady=false, cellsTh=null;
    var cfg=JSON.parse($('script[type="application/json"]',root).textContent), rings=cfg.rings;
    rings.forEach(function(r){ r.rgb=rgb(r.col); });
    var olive=getComputedStyle(pin).backgroundColor;
    function drawMap(){
      getLand().then(function(land){
        var d=fitCanvas(mapC), W=d.w, H=d.h, off=document.createElement('canvas'), step=Math.max(6,Math.round(7*DPR));
        off.width=Math.ceil(W/step); off.height=Math.ceil(H/step); var o=off.getContext('2d');
        // equirectangular crop around North America ↔ Europe
        var L0=-128,L1=42,A1=64,A0=14, sx=off.width/(L1-L0), sy=off.height/(A1-A0), s=Math.max(sx,sy), ox=(off.width-(L1-L0)*s)/2, oy=(off.height-(A1-A0)*s)/2;
        o.fillStyle='#fff'; o.beginPath();
        land.forEach(function(r){ r.forEach(function(pt,i){ var x=ox+(pt[0]-L0)*s, y=oy+(A1-pt[1])*s; i?o.lineTo(x,y):o.moveTo(x,y); }); o.closePath(); });
        o.fill('evenodd');
        var data=o.getImageData(0,0,off.width,off.height).data;
        mx.fillStyle='#0b0c09'; mx.fillRect(0,0,W,H);
        for(var y=0;y<off.height;y++) for(var x=0;x<off.width;x++){
          var on=data[(y*off.width+x)*4]>100, r2=rnd();
          if(on){ mx.fillStyle= r2<.08?'rgba('+SIGRGB+',.55)':'rgba(214,206,186,'+(.22+r2*.3)+')'; mx.fillRect(x*step+step*.2,y*step+step*.2,step*.55,step*.55); }
          else if(r2<.05){ mx.fillStyle='rgba(120,140,160,.12)'; mx.fillRect(x*step+step*.35,y*step+step*.35,step*.3,step*.3); }
        }
        mx.strokeStyle='rgba(244,241,234,.14)'; mx.lineWidth=1*DPR;
        for(var i=0;i<34;i++){ var bx=rnd()*W, by=rnd()*H, bs=(10+rnd()*40)*DPR; mx.strokeRect(bx,by,bs,bs); }
        mapReady=true;
      });
    }
    drawMap();
    var render=loop(root,function(t){
      var d=fitCanvas(pxC), W=d.w, H=d.h, p=P, cell=Math.max(26*DPR, Math.round(W/44));
      fitCanvas(rgC);
      var cols=Math.ceil(W/cell), rows=Math.ceil(H/cell);
      if(!cellsTh||cellsTh.length!==cols*rows){ cellsTh=[]; for(var i=0;i<cols*rows;i++){ var cx=(i%cols+.5)*cell, cy=(Math.floor(i/cols)+.5)*cell; cellsTh.push(Math.hypot((cx-W*.5)/W,(cy-H*1.05)/H)*.75+rnd()*.35); } }
      var r=seg(p,.14,.46)*1.25;
      px.clearRect(0,0,W,H);
      if(r<1.25){ for(var j=0;j<cellsTh.length;j++){ var th=cellsTh[j], x=(j%cols)*cell, y=Math.floor(j/cols)*cell;
          if(r>th+.07) continue;
          if(r>th){ var q=Math.random(); px.fillStyle=q<.4?'#e9e4d8':q<.7?'#8b8a80':'#000'; px.fillRect(x,y,cell,cell);
            if(q<.3){ px.fillStyle='rgba(0,0,0,.7)'; var sub=cell/4; for(var k=0;k<4;k++) px.fillRect(x+((Math.random()*4)|0)*sub,y+((Math.random()*4)|0)*sub,sub,sub); } }
          else { px.fillStyle=olive; px.fillRect(x,y,cell,cell); } } }
      mapC.style.opacity=1-seg(p,.84,.96)*.55;
      // rings
      rg.clearRect(0,0,W,H);
      var ra=seg(p,.44,.6), rf=seg(p,.58,.76), dm=seg(p,.8,.92), M=Math.min(W,H);
      rings.forEach(function(c,i){
        var cx=lerp(c.x,c.tx,eio(rf))*W, cy=lerp(c.y,c.ty,eio(rf))*H, R=c.r*M*(i===0?lerp(.9,1.15,eio(rf)):1);
        rg.lineWidth=(i===0?2:1.5)*DPR; rg.strokeStyle='rgba('+c.rgb+','+(ra*(1-dm*.6))+')';
        rg.beginPath(); rg.arc(cx,cy,R,-Math.PI/2,-Math.PI/2+6.2832*eio(ra)); rg.stroke();
        if(rf>0){ rg.fillStyle='rgba('+c.rgb+','+(rf*(i===0?.8:.5)*(1-dm*.7))+')'; rg.globalCompositeOperation=i===0?'source-over':'source-over'; rg.beginPath(); rg.arc(cx,cy,R*eo(rf),0,6.2832); rg.fill(); }
        var lb=lbls[i]; if(lb){ lb.style.left=(cx/DPR)+'px'; lb.style.top=(cy/DPR)+'px'; lb.style.opacity=seg(p,.5+i*.02,.6+i*.02)*(1-dm*.8); lb.style.fontSize=(i===0?Math.max(22,M/DPR*.07):Math.max(14,M/DPR*.035))+'px'; lb.style.color=i===0?'#000':'rgb('+c.rgb+')';
          if(rf>.5&&i>0) lb.style.color='#000'; }
      });
    });
    gsap.timeline({scrollTrigger:{trigger:pin,start:'top top',end:'+=280%',pin:true,scrub:true,onUpdate:function(s){ P=s.progress; render(); }}})
      .to(head,{yPercent:-60,opacity:0,ease:'power2.in',duration:.14},.3)
      .fromTo(end,{y:60,opacity:0},{y:0,opacity:1,duration:.12},.84)
      .to({}, {duration:.04},.96);
    var hw=maskWords($('[data-fx-rise]',head)||head); gsap.from(hw,{yPercent:110,duration:1.1,ease:'expo.out',stagger:.06,scrollTrigger:{trigger:root,start:'top 65%',once:true}});
    window.addEventListener('resize', function(){ cellsTh=null; clearTimeout(root._rt); root._rt=setTimeout(drawMap,200); });
  };

  /* ---- FRAME: orange frame → full bleed --------------------------------- */
  S.frame=function(root){
    var pin=$('.fx-pin',root), m=$('.fxfr__media',root);
    gsap.timeline({scrollTrigger:{trigger:pin,start:'top top',end:'+=130%',pin:true,scrub:true}})
      .fromTo(m,{clipPath:'inset(14% 12% 14% 12%)'},{clipPath:'inset(0% 0% 0% 0%)',ease:'power2.inOut',duration:.7},0)
      .fromTo($('img,video',m),{scale:1.3},{scale:1,ease:'none',duration:1},0)
      .fromTo($('.fxfr__h',root),{xPercent:0},{xPercent:-4,duration:1},0);
    var hw=maskWords($('.fxfr__h',root)); gsap.from(hw,{yPercent:110,duration:1.1,ease:'expo.out',stagger:.07,scrollTrigger:{trigger:root,start:'top 60%',once:true}});
  };

  /* ---- GALLERY: masonry parallax ---------------------------------------- */
  S.gallery=function(root){
    $$('.fxgl__it',root).forEach(function(it){
      var sp=parseFloat(it.getAttribute('data-speed')||0);
      gsap.fromTo(it,{y:sp*120},{y:-sp*120,ease:'none',scrollTrigger:{trigger:root,start:'top bottom',end:'bottom top',scrub:true}});
      gsap.fromTo(it,{clipPath:'inset(100% 0 0 0)'},{clipPath:'inset(0% 0 0 0)',duration:1.3,ease:'expo.inOut',scrollTrigger:{trigger:it,start:'top 95%',once:true}});
      gsap.to($('img',it),{scale:1,ease:'none',scrollTrigger:{trigger:it,start:'top bottom',end:'bottom top',scrub:true}});
    });
  };

  /* ---- HOW IT WORKS ----------------------------------------------------- */
  S.how=function(root){
    var top=$('.fxhw__top',root), img=$('.fxhw__img .fx-media',root), title=$('.fxhw__title',root);
    gsap.fromTo(img,{clipPath:'inset(10% 8% 10% 8%)'},{clipPath:'inset(0% 0% 0% 0%)',ease:'none',scrollTrigger:{trigger:top,start:'top bottom',end:'top top',scrub:true}});
    gsap.fromTo(title,{yPercent:40,opacity:.2},{yPercent:-30,opacity:1,ease:'none',scrollTrigger:{trigger:top,start:'top bottom',end:'bottom top',scrub:true}});
    $$('.fxhw__row .fx-media img',root).forEach(function(im){ gsap.fromTo(im,{yPercent:-10,scale:1.2},{yPercent:10,ease:'none',scrollTrigger:{trigger:im.parentNode,start:'top bottom',end:'bottom top',scrub:true}}); });
    $$('.fxhw__row',root).forEach(function(r){ gsap.from($$('p',r),{y:40,opacity:0,duration:1.1,ease:'expo.out',scrollTrigger:{trigger:r,start:'top 80%',once:true}}); });
  };

  /* ---- FOOTER: blend field + wordmark fill ------------------------------ */
  S.footer=function(root){
    var bl=$('.fxft__blend',root), cv=bl&&$('canvas',bl), P=0;
    if(cv){
      var ctx=cv.getContext('2d'), word=bl.getAttribute('data-word')||'FALL IN', mask=null, mw=0, mh=0, rnd=mulberry(9), drops=[];
      for(var i=0;i<14;i++) drops.push({x:rnd(),y:rnd()*.5,r:.02+rnd()*.05,ph:rnd()*6});
      var oliveC=getComputedStyle(root).backgroundColor;
      function buildMask(W,H,cell){
        var o=document.createElement('canvas'); o.width=Math.ceil(W/cell); o.height=Math.ceil(H/cell); var c=o.getContext('2d');
        c.fillStyle='#fff'; c.textAlign='center'; c.textBaseline='middle'; var fs=o.width/(word.length*.62); c.font='700 '+fs+'px "Chakra Petch",sans-serif';
        c.fillText(word,o.width/2,o.height*.36); mask={d:c.getImageData(0,0,o.width,o.height).data,w:o.width,h:o.height,cell:cell};
      }
      function edge(W,H,level,t,amp,seed){ // wavy top edge y for region rising from bottom
        ctx.beginPath(); ctx.moveTo(0,H);
        for(var x=0;x<=W;x+=W/60){ var u=x/W, y=H*(1-level)+amp*H*(Math.sin(u*7+seed+t*.5)*.5+Math.sin(u*13-seed*2+t*.3)*.3+Math.sin(u*3.3+seed)*.6); ctx.lineTo(x,y); }
        ctx.lineTo(W,H); ctx.closePath(); ctx.fill();
      }
      var render=loop(bl,function(t){
        var d=fitCanvas(cv), W=d.w, H=d.h, p=P, cell=Math.max(7*DPR,Math.round(W/170));
        if(!mask||mask.cell!==cell) buildMask(W,H,cell);
        ctx.fillStyle='#070706'; ctx.fillRect(0,0,W,H);
        var o=eo(seg(p,0,.45));
        ctx.fillStyle=SIG; edge(W,H,o*1.15,t,.09*(1-o*.6),1.3);
        drops.forEach(function(dr){ var y=H*(1-o*1.1)-dr.y*H*.4*(1-o); ctx.beginPath(); ctx.arc(dr.x*W,y+Math.sin(t+dr.ph)*6*DPR,dr.r*W*(1-o*.5),0,6.2832); ctx.fill(); });
        // dot-matrix word
        if(o>.2){ var a=seg(o,.2,.8); ctx.fillStyle='rgba(255,'+Math.min(255,SIGRGB[1]+80)+','+Math.min(255,SIGRGB[2]+70)+','+(.75*a)+')';
          var sh=((t*6)|0); for(var y=0;y<mask.h;y++) for(var x=0;x<mask.w;x++){ var on=mask.d[(y*mask.w+x)*4]>90; if(on&&((x+y+sh)%9!==0)){ ctx.fillRect(x*cell+cell*.18,y*cell+cell*.18,cell*.64,cell*.64); } else if(!on && (x*7+y*13+sh)%23===0){ ctx.fillRect(x*cell+cell*.4,y*cell+cell*.4,cell*.2,cell*.2); } } }
        var ol=eo(seg(p,.4,1));
        if(ol>0){ ctx.fillStyle=oliveC; edge(W,H,ol*.72,t*1.2,.08,4.1);
          for(var k=0;k<7;k++){ var bx=(k*.17+.05)*W, by=H*(1-ol*.72)-(k%3)*H*.06-H*.05; ctx.beginPath(); ctx.arc(bx,by+Math.sin(t*.8+k)*5*DPR,(18+k%4*14)*DPR*ol,0,6.2832); ctx.fill(); } }
      });
      ScrollTrigger.create({trigger:bl,start:'top bottom',end:'bottom 40%',scrub:true,onUpdate:function(s){ P=s.progress; render(); }});
    }
    var fill=$('.fxft__fill',root);
    if(fill) gsap.fromTo(fill,{attr:{width:0}},{attr:{width:1060},ease:'none',scrollTrigger:{trigger:$('.fxft__word',root),start:'top bottom',end:'bottom 85%',scrub:true}});
    var top=$('[data-fx-top]',root); if(top) top.addEventListener('click',function(e){ e.preventDefault(); lenis?lenis.scrollTo(0,{duration:2}):window.scrollTo({top:0,behavior:'smooth'}); });
  };

  function initSection(el){
    var k=el.getAttribute('data-fxs'); if(!k||!S[k]||el._fx) return; el._fx=1;
    try{ S[k](el); }catch(err){ console.warn('[endex-fx]',k,err); }
  }

  /* =================================================================== BOOT */
  ready(function(){
    if(REDUCE || !window.gsap || !window.ScrollTrigger){ html.classList.remove('fx-load'); var l=$('.fxl'); l&&l.remove();
      $$('[data-fx-top]').forEach(function(a){ a.addEventListener('click',function(e){ e.preventDefault(); window.scrollTo({top:0}); }); });
      return; }
    html.classList.add('fx-on');
    core();
    header();
    $$('[data-fxs]').forEach(initSection);
    globals();
    loader();
    var fontsDone=document.fonts&&document.fonts.ready;
    (fontsDone||Promise.resolve()).then(function(){ ScrollTrigger.refresh(); });
    window.addEventListener('load', function(){ ScrollTrigger.refresh(); });
    document.addEventListener('shopify:section:load', function(e){ $$('[data-fxs]', e.target).forEach(initSection); ScrollTrigger.refresh(); });
  });
})();
